// -----------------------------------------------------------------------------
// A3.2 Publish history (pop-up from the clock icon on A3, and from report rows in Activity) and
// A3.3 Sign-off (pop-up from a Sign-off chip on A3). Part 5.
//   Data: rpc issues_publish_history({code, prefix?}) → PublishHistory (versions newest first).
//   Sign-off save: rpc issues_signoff_save({code, id, signed, signed_on, remarks}) — roles with the Sign-off switch only;
//   others see the pop-up read-only (the (i) says why). Unsaved changes ask Save / Discard / Keep editing (useAskClose).
// -----------------------------------------------------------------------------
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { PublishedVersion, PublishHistory as History } from "../../../core/api/types";
import { rpc } from "../../../core/api/backend";
import { useRpc } from "../../../core/api/useRpc";
import { DATE_HINT_ROW, formatDate } from "../../../core/format";
import { useEsc } from "../../../core/esc";
import { DateInput, ErrorBox, InfoIcon, Loading, Modal, Switch, toast, useAskClose } from "../../../core/ui/kit";
import { Icon } from "../../../core/ui/icons";

export const HISTORY_TIP = ["Every export of a component becomes its next version (V00, V01…).", "Sign-off is recorded against a published version.", "The PDFs themselves are not stored."];

/** "2026-02-26T17:30" → "26/Feb/26 17:30" */
const stamp = (ts: string) => `${formatDate(ts.slice(0, 10))} ${ts.slice(11, 16)}`;

/** A3.2 — components on the left, the picked component's versions on the right. */
export function PublishHistoryDialog({ code, initial, onClose }: { code: string; initial?: string; onClose: () => void }) {
  const { data, error } = useRpc("issues_publish_history", { code });
  const [pick, setPick] = useState<string | null>(initial ?? null);
  const comps = data?.components ?? [];
  const cur = comps.find((c) => c.prefix === pick) ?? comps.find((c) => c.versions.length) ?? comps[0];
  return (
    <Modal title="Publish history" info={HISTORY_TIP} onClose={onClose} className="history-modal">
      {error ? <ErrorBox message={error} /> : !data ? <Loading /> : (
        <div className="hist">
          <div className="hist-list" role="listbox" aria-label="Components">
            <div className="hist-li head"><span>Component</span><span>Latest</span></div>
            {comps.map((c) => (
              <button key={c.id} role="option" aria-selected={c === cur} className={`hist-li${c === cur ? " on" : ""}`} data-history={c.prefix} onClick={() => setPick(c.prefix)}>
                <span className="ell"><span className="code">{c.prefix}</span>&nbsp;&nbsp;{c.name}</span>
                <b className={c.versions.length ? "" : "faint"}>{c.versions[0]?.version ?? "–"}</b>
              </button>))}
          </div>
          {cur && <div className="hist-detail" data-testid="history-detail">
            <h3><span className="code">{cur.prefix}</span> {cur.name}</h3>
            {cur.versions.length === 0 ? <p className="muted">Not published yet.</p> : (
              <table className="hist-table">
                <thead><tr><th>Version</th><th>Published</th><th>Signed off</th><th>Signed off by</th><th>Remarks</th></tr></thead>
                <tbody>{cur.versions.map((v, k) => (
                  <tr key={v.id} data-version={v.version}>
                    <td className="nowrap">{/* v0.9.1 (round 32 #12): the same coloured chip as A3's Sign-off column */}
                      <span className={`so-chip ${verState(v)}`} data-ver-chip={verState(v)}>{v.version}</span>{k === 0 && <span className="pill p-latest">latest</span>}</td>
                    <td>{v.published_by ?? "—"}<br /><span className="muted small">{stamp(v.published_at)}</span></td>
                    <td>{v.signed_on ? <span className="pill p-closed"><Icon name="check" /> {formatDate(v.signed_on)}</span> : "–"}</td>
                    <td>{v.signed_by ? <>{v.signed_by}{v.signed_by_role && <><br /><span className="muted small">{v.signed_by_role}</span></>}</> : "–"}</td>
                    <td className="muted">{v.remarks || "–"}</td>
                  </tr>))}
                </tbody>
              </table>)}
          </div>}
        </div>)}
      <div className="modal-foot"><button className="btn" onClick={onClose}>Close</button></div>
    </Modal>
  );
}

const SIGNOFF_KEY = (
  <div className="so-key">
    <b>Sign-off legend</b>
    <p><span className="so-chip signed">V01</span>Signed off</p>
    <p><span className="so-chip awaiting">V02</span>Awaiting sign-off (exported with signing page)</p>
    <p><span className="so-chip published">V01</span>Published, not sent for sign-off</p>
    <p className="so-key-note">Only versions exported <b>with the signing page</b> can be signed off.</p>
    <p className="so-key-note">Only roles with <b>Sign-off</b> access (Admin console) can switch this on. Your name is logged when you save.</p>
  </div>
);
const SIGNOFF_TIP = "Sign-off legend: green = signed off · amber = awaiting sign-off (exported with signing page) · white = published, not sent for sign-off. Only versions exported with the signing page can be signed off. Only roles with Sign-off access (Admin console) can switch this on. Your name is logged when you save.";
/** v0.7.2 (round 26 #2, 0018): a version can be switched to signed off only when it went out with the signing page;
 *  one signed before that rule keeps its sign-off (and can still be edited or removed). Pure — unit-tested. */
/** v0.9.1 (round 32 #12): chip colour of a version — green signed off · amber sent for sign-off (with signing page) · white not sent. */
export const verState = (v: Pick<PublishedVersion, "with_signing_page" | "signed_on">) => (v.signed_on ? "signed" : v.with_signing_page ? "awaiting" : "published");
export const canSignOff = (v: Pick<PublishedVersion, "with_signing_page" | "signed_on">) => v.with_signing_page || !!v.signed_on;

/** A3.3 — anchored under the chip; picks a published version and switches its sign-off on / off. */
export function SignoffPopup({ code, prefix, anchor, onClose }: { code: string; prefix: string; anchor: DOMRect; onClose: (changed: boolean) => void }) {
  const { data, error } = useRpc("issues_publish_history", { code, prefix });
  return data ? <SignoffForm code={code} h={data} anchor={anchor} onClose={onClose} />
    : <Panel anchor={anchor} onOutside={() => onClose(false)}>{error ? <ErrorBox message={error} /> : <Loading />}</Panel>;
}

/** Floating panel: a see-through backdrop catches outside clicks; the calendar (z 120) stays clickable above it. */
function Panel({ anchor, onOutside, children }: { anchor: DOMRect; onOutside: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  // Board A3.3: the panel sits to the left of the chip, its top near the chip; kept inside the window, and moved again
  // whenever its height changes (version list opened, error shown).
  useLayoutEffect(() => {
    const el = ref.current!;
    const place = () => {
      const m = el.getBoundingClientRect(), vw = window.innerWidth, vh = window.innerHeight;
      let left = anchor.left - 10 - m.width;
      if (left < 8) left = Math.min(anchor.right - m.width, vw - 8 - m.width);
      const top = Math.min(Math.max(8, anchor.top - 30), Math.max(8, vh - 8 - m.height));
      setPos({ left: Math.max(8, left), top });
    };
    place();
    const ro = new ResizeObserver(place); ro.observe(el);
    return () => ro.disconnect();
  }, [anchor]);
  return (
    <div className="so-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onOutside()}>
      <div ref={ref} className="so-panel" role="dialog" aria-label="Sign-off" style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999 }}>{children}</div>
    </div>
  );
}

function SignoffForm({ code, h, anchor, onClose }: { code: string; h: History; anchor: DOMRect; onClose: (changed: boolean) => void }) {
  const comp = h.components[0];
  // opens on the newest version that can be signed off (else the newest)
  const first = comp.versions.find(canSignOff) ?? comp.versions[0];
  const [sel, setSel] = useState<PublishedVersion>(first);
  const blank = (v: PublishedVersion) => ({ signed: !!v.signed_on, on: v.signed_on ?? null, remarks: v.remarks });
  const [form, setForm] = useState(blank(first));
  const [list, setList] = useState(false);
  const [dateOk, setDateOk] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(blank(sel)), [form, sel]);
  const save = async () => {
    if (form.signed && (!form.on || !dateOk)) { setErr(`Signed on: ${DATE_HINT_ROW}`); return; }
    setSaving(true); setErr(null);
    try {
      await rpc("issues_signoff_save", { code, id: sel.id, signed: form.signed, signed_on: form.on, remarks: form.remarks });
      toast(form.signed ? `${comp.prefix} ${sel.version} signed off` : `${comp.prefix} ${sel.version} sign-off removed`);
      onClose(true);
    } catch (e) { setErr((e as Error).message); setSaving(false); }
  };
  const { close, confirm } = useAskClose(dirty && h.can_signoff, () => onClose(false), save);
  useEsc("popup", () => { if (list) { setList(false); return true; } close(); return true; });
  const choose = (v: PublishedVersion) => { setSel(v); setForm(blank(v)); setList(false); setErr(null); };
  const noSheet = !canSignOff(sel);
  const ro = !h.can_signoff || noSheet;
  return (
    <Panel anchor={anchor} onOutside={close}>
      <div className="so-head"><h3><span className="code">{comp.prefix}</span> {comp.name} · sign-off</h3><InfoTipKey />
        <span className="grow" /><button className="btn icon mini" aria-label="Close" onClick={close}><Icon name="close" /></button></div>
      <p className="so-k">Published version</p>
      <button type="button" className={`so-pick${list ? " open" : ""}`} aria-expanded={list} aria-label="Published version" onClick={() => setList(!list)}>
        <span><b>{sel.version}</b> · {sel.published_at ? formatDate(sel.published_at.slice(0, 10)) : ""} · {sel.published_by ?? "—"}</span><span className="caret"><Icon name="arrow_drop_down" /></span></button>
      {list && <div className="so-list" role="listbox">{comp.versions.map((v) => (
        <button key={v.id} role="option" aria-selected={v.id === sel.id} className={`so-opt${v.id === sel.id ? " on" : ""}${canSignOff(v) ? "" : " no-sheet"}`}
          data-so-version={`${v.version}:${v.with_signing_page ? "sent" : "no-sheet"}`} onClick={() => choose(v)}>
          <span>{v.version}{v.with_signing_page ? " · sent" : " · no signing page"}{v.signed_on && <> · <Icon name="check" /></>}</span><span className="muted">{formatDate(v.published_at.slice(0, 10))}</span></button>))}
      </div>}
      <div className="so-switch"><span>Signed off</span>
        <Switch on={form.signed} label="Signed off" disabled={ro} onChange={(v) => setForm({ ...form, signed: v, on: v ? form.on ?? h.today : form.on })} /></div>
      {noSheet && h.can_signoff && <p className="muted small so-why" data-testid="so-no-sheet">{sel.version} was exported without the signing page, so it can't be signed off. Export a new version with <b>Include signing page</b> ticked.</p>}
      <p className="so-k">Signed on</p>
      {form.signed && !ro ? <DateInput value={form.on} onChange={(v) => { setForm({ ...form, on: v }); setErr(null); }} onValidity={setDateOk} ariaLabel="Signed on" invalid={!!err} />
        : <input disabled value={form.on ? formatDate(form.on) : ""} placeholder="DD/Mmm/YY" aria-label="Signed on" />}
      <p className="so-k">Remarks</p>
      <input value={form.remarks} disabled={ro || !form.signed} aria-label="Remarks" maxLength={500} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
      {err && <p className="error-inline" role="alert">{err}</p>}
      <div className="modal-foot"><button className="btn" onClick={close}>Cancel</button>
        <button className="btn primary" disabled={ro || !dirty || saving} onClick={save}>Save</button></div>
      {confirm}
    </Panel>
  );
}

/** The (i) beside the A3.3 title shows the sign-off key with the real chip colours (board A3.3 tooltip). */
function InfoTipKey() { return <InfoIcon tip={SIGNOFF_TIP} content={SIGNOFF_KEY} />; }
